import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { LyricTimingLine, needsCheck } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { formatLineTime, NUDGE_MS, NUDGE_SHIFT_MS } from './lyricTimingFormat';
import { lyricTimingMessages } from './lyricTimingMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric timing; each part uses its own slice of it */
import styles from './LyricTiming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricTimingRowProps {
  line: LyricTimingLine;
  /** The master plays this line now: the violet tint. */
  isNow: boolean;
  isSelected: boolean;
  /** False while a sync runs: no − and +. */
  canEdit: boolean;
  /** False while the master cannot play. */
  canPlay: boolean;
  onPlay: (index: number) => void;
  onSelect: (index: number) => void;
  onNudge: (index: number, deltaMs: number) => void;
}

/**
 * One line of the sync editor (design s8): ▶, the start, − and +, the text and the dot. The dot is
 * violet for a time that a member set, amber for a time to check. A line with no time shows
 * `—:—` in grey, and ▶ and − / + are off.
 */
const LyricTimingRowComponent: React.FC<LyricTimingRowProps> = ({
  line,
  isNow,
  isSelected,
  canEdit,
  canPlay,
  onPlay,
  onSelect,
  onNudge,
}) => {
  const intl = useIntl();
  const { index } = line;
  const hasTime = line.startMs !== null;
  const time = formatLineTime(line.startMs);
  const check = needsCheck(line);

  const play = useCallback(() => onPlay(index), [index, onPlay]);
  const select = useCallback(() => onSelect(index), [index, onSelect]);
  const earlier = useCallback(
    (event: React.MouseEvent) => onNudge(index, -(event.shiftKey ? NUDGE_SHIFT_MS : NUDGE_MS)),
    [index, onNudge]
  );
  const later = useCallback(
    (event: React.MouseEvent) => onNudge(index, event.shiftKey ? NUDGE_SHIFT_MS : NUDGE_MS),
    [index, onNudge]
  );

  let dotClass: string | undefined;
  let dotLabel: string | undefined;
  if (line.edited) {
    dotClass = styles.dotEdited;
    dotLabel = intl.formatMessage(lyricTimingMessages.dotChanged);
  } else if (check) {
    dotClass = styles.dotCheck;
    dotLabel = intl.formatMessage(lyricTimingMessages.dotCheck);
  }

  return (
    <div
      className={clsx(
        styles.row,
        !canEdit && styles.rowReadOnly,
        isNow && styles.rowNow,
        isSelected && styles.rowSelected,
        line.gapBefore && styles.rowGap
      )}
      data-line-index={index}
      data-testid={`lyric-line-${index}`}
      aria-current={isNow ? 'true' : undefined}
    >
      <button
        type="button"
        className={styles.play}
        onClick={play}
        disabled={!canPlay || !hasTime}
        tabIndex={-1}
        aria-label={
          hasTime
            ? intl.formatMessage(lyricTimingMessages.playFrom, { time })
            : intl.formatMessage(lyricTimingMessages.playFromNoTime)
        }
      >
        ▶
      </button>
      <span
        className={clsx(styles.time, line.edited && styles.timeEdited, !hasTime && styles.timeMissing)}
        data-testid={`lyric-time-${index}`}
      >
        {time}
      </span>
      {canEdit && (
        <span className={styles.nudge}>
          <button
            type="button"
            onClick={earlier}
            disabled={!hasTime}
            tabIndex={-1}
            aria-label={intl.formatMessage(lyricTimingMessages.earlier)}
          >
            −
          </button>
          <button
            type="button"
            onClick={later}
            disabled={!hasTime}
            tabIndex={-1}
            aria-label={intl.formatMessage(lyricTimingMessages.later)}
          >
            +
          </button>
        </span>
      )}
      {/* A click on the text selects the line for the keys. The list handles the keys, so the
          text is out of the tab order. */}
      <button type="button" className={styles.text} onClick={select} tabIndex={-1}>
        {line.text}
      </button>
      <span
        className={clsx(styles.dot, dotClass)}
        title={dotLabel}
        aria-label={dotLabel}
        role={dotLabel === undefined ? undefined : 'img'}
      />
    </div>
  );
};

export const LyricTimingRow = TypedMemo(LyricTimingRowComponent);
